//@ts-check

/**
 * For the "Branding" license the user is asked:
 *
 * "The fonts will be used for:"
 * - "Myself or my company (own project)"
 * - "A client (commissioned project)"
 *
 * The element is rendered on the backend by:
 * - Licensing::LicenseUsageSelectorComponent
 *
 * Purpose:
 * - listen to radio + client-name field and call `onChange` with
 *   `{ licenseUsage, licenseUsageClientName }` (for checkout_form_controller, etc.)
 *
 */
export class LicenseUsageSelectorComponent {
  /**
   * @param {{ element: HTMLElement, onChange?: (p: { licenseUsage: "client" | "me", licenseUsageClientName: string }) => void }} args
   */
  constructor({ element, onChange }) {
    this.element = element;
    this.onChange = onChange;
    this.addListeners();
  }

  /** @returns {"client" | "me"} */
  get value() {
    const checked = this.element.querySelector(
      'input[name="license_usage"]:checked'
    );
    const value = checked && "value" in checked ? String(checked.value) : "";

    if (value === "client" || value === "me") {
      return value;
    }

    throw new Error(`LicenseUsageSelectorComponent: invalid value: ${value}`);
  }

  /** @returns {string} */
  get licenseUsageClientName() {
    const el = this.element.querySelector(
      'input[name="license_usage_client_name"]'
    );
    return el instanceof HTMLInputElement ? el.value : "";
  }

  /**
   * Sync radios from `applyState` / server. Programmatic `checked` does not emit `change`.
   * @param {"client" | "me"} licenseUsage
   */
  setLicenseUsage(licenseUsage) {
    if (licenseUsage !== "client" && licenseUsage !== "me") return;
    const radio = this.element.querySelector(
      `input[name="license_usage"][value="${licenseUsage}"]`
    );
    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
    }
  }

  /**
   * Sync the client name field. Programmatic `value` does not emit `input`.
   * @param {string} name
   */
  setLicenseUsageClientName(name) {
    const el = this.clientNameInput;
    if (!(el instanceof HTMLInputElement)) return;
    el.value = name;
  }

  addListeners() {
    if (!this.element) return;

    this._notify = () => {
      this.onChange({
        licenseUsage: this.value,
        licenseUsageClientName: this.licenseUsageClientName,
      });
    };
    this.element
      .querySelectorAll('input[name="license_usage"]')
      .forEach((radio) => {
        radio.addEventListener("change", this._notify);
      });
    this.clientNameInput = this.element.querySelector(
      'input[name="license_usage_client_name"]'
    );
    this.clientNameInput?.addEventListener("input", this._notify);
    this._notify();
  }

  removeListeners() {
    if (!this.element) return;

    this.element
      .querySelectorAll('input[name="license_usage"]')
      .forEach((radio) => {
        radio.removeEventListener("change", this._notify);
      });
    this.clientNameInput?.removeEventListener("input", this._notify);
  }
}
